import type { Control, ControlValue } from "./control.js";
import type { ToolOptions } from "./options.js";

/**
 * The pinyin settings panel's table: what each setting is called, and what it
 * does.
 *
 * Separate from `options.ts`, which holds the shape of the settings and the
 * translation back to what the package wants. This file is the copy — the
 * labels a learner reads and the sentence under each row — and it changes for
 * editorial reasons rather than for behavioural ones.
 */

/**
 * Booleans get named choices rather than a checkbox, so that every control in
 * the panel is the same shape and so that a setting that is off reads as a
 * choice somebody made rather than a box nobody ticked.
 */
const onOff: readonly ControlValue[] = [
  { value: true, label: "On" },
  { value: false, label: "Off" },
];

/**
 * The settings panel, in the order it is shown.
 *
 * Ordered by how often somebody wants to change it, not by how the package
 * lists them: tone notation is the first thing a teacher reaches for and the
 * Taiwan readings are the last.
 */
export const controls: readonly Control<keyof ToolOptions>[] = [
  {
    field: "notation",
    label: "Tones",
    note: "Tone marks are standard. Numbers are easier to type and to search for; superscript keeps the word searchable either way.",
    values: [
      { value: "marks", label: "Marks — nǐ hǎo" },
      { value: "numbers", label: "Numbers — ni3 hao3" },
      { value: "superscript", label: "Superscript — ni³ hao³" },
      { value: "none", label: "None — ni hao" },
    ],
  },
  {
    field: "grouping",
    label: "Word spacing",
    note: "Standard pinyin spaces by word rather than by syllable: 南京市 is Nánjīng Shì, not Nán jīng shì.",
    values: onOff,
  },
  {
    field: "capitals",
    label: "Capital letters",
    note: "Automatic capitalises proper nouns and the first word of each sentence, which needs the Chinese to be punctuated.",
    values: [
      { value: "auto", label: "Automatic" },
      { value: "proper", label: "Proper nouns only" },
      { value: "none", label: "None" },
    ],
  },
  {
    field: "punctuation",
    label: "Punctuation",
    note: "Whether 。，、；：？！ become . , ; : ? ! with the spacing English uses.",
    values: [
      { value: "latin", label: "Convert to Latin" },
      { value: "keep", label: "Keep Chinese" },
    ],
  },
  {
    field: "numbers",
    label: "Numbers",
    note: "Read says the digits aloud, and takes its cue from what follows: 1998年 is yī jiǔ jiǔ bā nián but 3个 is sān gè.",
    values: [
      { value: "read", label: "Read aloud" },
      { value: "keep", label: "Keep digits" },
    ],
  },
  {
    field: "yiBu",
    label: "一 and 不 tone changes",
    note: "The tone changes these two characters take from what follows them: 不是 is bú shì.",
    values: onOff,
  },
  {
    field: "thirdTone",
    label: "Third-tone sandhi",
    note: "Off by default, because standard pinyin writes the underlying tone: 你好 is written nǐ hǎo even though it is said ní hǎo.",
    values: onOff,
  },
  {
    field: "apostrophe",
    label: "Apostrophes",
    note: "The 隔音符号, which keeps a syllable boundary readable. Standard writes it only where leaving it out would be ambiguous: Xī'ān, but Tiānānmén.",
    values: [
      { value: "always", label: "Always — Xī'ān" },
      { value: "standard", label: "Where needed" },
      { value: "never", label: "Never — Xīān" },
    ],
  },
  {
    field: "locale",
    label: "Reading standard",
    note: "Where the mainland and Taiwan differ: 垃圾 is lājī in 普通话 and lèsè in 國語.",
    values: [
      { value: "zh-CN", label: "Mainland — 普通话" },
      { value: "zh-TW", label: "Taiwan — 國語" },
    ],
  },
];
